/**
 * `renderCitation` — single converged formatter for refKey → lawyer-facing string.
 *
 * Replaces the five divergent codepaths the audit identified:
 *   - `lib/inline-citations.ts::humanizeRefKey` (Compare cells)
 *   - `ui/components/editor/citation-format.ts::formatCitation` (TipTap chips, Authorities, Sources)
 *   - `ui/components/agent/citation-chip.tsx::parseRefKey` (Agent chat fallback orphan)
 *   - raw refKey display in `compare/CorpusHitsDrawer`, `carte/LoiStrip`
 *
 * Convention: code-first NF Z44-005 (Dalloz / Cour de cassation usage),
 * NBSP-protected atomic tokens so narrow columns wrap at the comma
 * rather than mid-citation. Single example: `C.\u00A0civ., art.\u00A01240`.
 */
import type { CitationTemplate, RenderCitationInput } from '../entity'

/** Unicode non-breaking space, kept inside atomic tokens. */
const NBSP = '\u00A0'

/** Replace every regular space inside a fragment with NBSP so the
 *  line-breaker treats the fragment as atomic. */
function atomic(s: string): string {
  return s.replace(/ /g, NBSP)
}

/** Canonical FR + MA code abbreviations keyed by slug. Mirrors the
 *  previous editor table verbatim — that table was reviewed by the
 *  founder during Sprint 3 PR-2b and is the most complete source of
 *  truth in the codebase. */
const SLUG_TO_ABBR: Record<string, string> = {
  'code-civil': 'C. civ.',
  'code-commerce': 'C. com.',
  'code-procedure-civile': 'CPC',
  'code-penal': 'C. pén.',
  'code-procedure-penale': 'CPP',
  'code-consommation': 'C. consom.',
  'code-travail': 'C. trav.',
  'code-sante-publique': 'CSP',
  'code-securite-sociale': 'CSS',
  'code-monetaire-financier': 'CMF',
  'code-general-impots': 'CGI',
  'code-construction-habitation': 'CCH',
  'code-rural-peche-maritime': 'CRPM',
  'code-environnement': 'C. env.',
  'code-route': 'C. route',
  'code-civil-marocain': 'DOC',
  'code-procedure-civile-marocaine': 'CPC (MA)',
  'code-commerce-marocain': 'C. com. (MA)',
}

/** Reverse lookup — accent-/case-insensitive — for when the BE
 *  returns the full name `Code de procédure civile` instead of the
 *  slug. */
const FULL_NAME_TO_ABBR: Record<string, string> = {
  'code civil': 'C. civ.',
  'code de commerce': 'C. com.',
  'code de procedure civile': 'CPC',
  'code penal': 'C. pén.',
  'code de procedure penale': 'CPP',
  'code de la consommation': 'C. consom.',
  'code du travail': 'C. trav.',
  'code de la sante publique': 'CSP',
  'code de la securite sociale': 'CSS',
  'code monetaire et financier': 'CMF',
  'code general des impots': 'CGI',
  'code de la construction et de l habitation': 'CCH',
  'code rural et de la peche maritime': 'CRPM',
  'code de l environnement': 'C. env.',
  'code de la route': 'C. route',
  'dahir des obligations et contrats': 'DOC',
  'code des obligations et contrats': 'DOC',
}

/** Cour de cassation: court label table. */
const JP_COURT_LABEL: Record<string, string> = {
  ccass: 'Cass.',
  ce: 'CE',
  cc: 'Cons. const.',
  ca: 'CA',
  cjue: 'CJUE',
  cedh: 'CEDH',
}

/** Cour de cassation chamber inference from the `caseId` prefix. */
const CCASS_CHAMBER_FROM_PREFIX: ReadonlyArray<readonly [RegExp, string]> = [
  [/^co/i, 'com.'],
  [/^civ1/i, 'civ. 1'],
  [/^civ2/i, 'civ. 2'],
  [/^civ3/i, 'civ. 3'],
  [/^crim/i, 'crim.'],
  [/^soc/i, 'soc.'],
  [/^as/i, 'ass. plén.'],
  [/^mi/i, 'ch. mixte'],
]

/** Normalise a code identifier (lowercase, ASCII-fold, single-space). */
function normaliseCodeKey(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['\u2019]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Resolve a code identifier (slug or full name) to its lawyer-facing
 *  abbreviation. Returns `null` when nothing matches so callers can
 *  decide between "show the raw value title-cased" and "drop the
 *  prefix entirely". Public — `use-editor-authorities` consumes this
 *  for the Autorités panel grouping. */
export function resolveCodeAbbreviation(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null
  if (SLUG_TO_ABBR[raw]) return SLUG_TO_ABBR[raw]
  const normal = normaliseCodeKey(raw)
  if (FULL_NAME_TO_ABBR[normal]) return FULL_NAME_TO_ABBR[normal]
  return null
}

/** Title-case a slug as a graceful fallback (`code-de-truc` → `Code De Truc`). */
function titleCaseSlug(slug: string): string {
  return slug
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')
}

/** Format an ECLI jurisprudence ref. BE-supplied `label` wins outright
 *  (BE often has the full pourvoi number + exact date the ECLI doesn't
 *  encode). Otherwise, parse `ecli:fr:ccass:2020:co00193` →
 *  `Cass. com., 2020, n°\u00A0CO00193`. */
function renderJurisprudence(refKey: string, label: string | null): string {
  if (label && label.trim()) return label.trim()
  const parts = refKey.replace(/^ecli:/, '').split(':')
  if (parts.length < 4) return refKey.toUpperCase()
  const [, court, year, caseId] = parts
  const courtLabel = JP_COURT_LABEL[court.toLowerCase()] ?? court.toUpperCase()
  let chamber: string | null = null
  if (court.toLowerCase() === 'ccass') {
    for (const [re, lab] of CCASS_CHAMBER_FROM_PREFIX) {
      if (re.test(caseId)) {
        chamber = lab
        break
      }
    }
  }
  const courtFull = chamber
    ? atomic(`${courtLabel} ${chamber}`)
    : atomic(courtLabel)
  return `${courtFull}, ${year}, n°${NBSP}${caseId.toUpperCase()}`
}

/** Default inline format when no template is supplied — NF Z44-005
 *  (code first, NBSP-linked `art. N`). Matches the shipped behaviour
 *  pre-P1.A so callers that don't pass a template see no change. */
const DEFAULT_INLINE_FORMAT = '{code}, art.\u00A0{article}'

/** Substitute `{code}` and `{article}` placeholders in the template's
 *  `inlineFormat`. Placeholder values are atomicised (regular spaces
 *  → NBSP) so the wrap happens at the template's literal whitespace,
 *  not inside `C. civ.` or `art. 1240`. */
function applyTemplate(
  inlineFormat: string,
  codeAbbr: string,
  articleNum: string,
): string {
  return inlineFormat
    .replaceAll('{code}', atomic(codeAbbr))
    .replaceAll('{article}', atomic(articleNum))
}

/** Format an article refKey. Registry overrides (`codeName`,
 *  `articleNumber`) take precedence over the parsed refKey, so a
 *  hydrated registry hit always renders the best label we have.
 *  When `template` is provided, its `inlineFormat` drives the layout;
 *  otherwise the default NF Z44-005 form is used. */
function renderArticle(
  refKey: string,
  codeName: string | null,
  articleNumber: string | null,
  template: CitationTemplate | undefined,
): string {
  const [rawArt, slug] = refKey.split('|')
  const artNum =
    articleNumber ?? rawArt?.match(/^art(.+)$/i)?.[1] ?? rawArt ?? ''
  const abbrFromCodeName = resolveCodeAbbreviation(codeName)
  const abbrFromSlug = resolveCodeAbbreviation(slug)
  const abbr =
    abbrFromCodeName ??
    abbrFromSlug ??
    (codeName?.trim() || (slug ? titleCaseSlug(slug) : null))
  const inlineFormat = template?.inlineFormat ?? DEFAULT_INLINE_FORMAT
  if (abbr && artNum) return applyTemplate(inlineFormat, abbr, artNum)
  // Partial data — render whichever atom we have. Template doesn't
  // apply here: the template assumes both `{code}` and `{article}`
  // are present, so partial cases bypass it.
  if (artNum) return atomic(`art. ${artNum}`)
  if (abbr) return atomic(abbr)
  return refKey
}

/**
 * Render a refKey to its lawyer-facing citation string.
 *
 * Behaviour:
 *   - `ecli:…` prefix → jurisprudence path (BE label wins, else ECLI parse).
 *   - `art<N>|<slug>|<juris>` → article path (registry overrides win,
 *     `template.inlineFormat` drives the layout when present).
 *   - Anything else → return `refKey` verbatim (last-resort fallback so
 *     malformed input never silently disappears).
 *
 * Atomic tokens carry NBSP between their parts; the breakable space
 * is the one the template author wrote with a regular space (typically
 * after the comma between code and article). Forward-compatible with
 * the lawyer-editable templates (P1.B Settings UI) — callers pass the
 * resolved template via `useCitationTemplate(juris, lang)` and the
 * same function handles both default + user-customised forms.
 */
export function renderCitation(
  input: RenderCitationInput,
  template?: CitationTemplate,
): string {
  const { refKey, codeName, articleNumber, label } = input
  if (refKey.startsWith('ecli:')) {
    // Jurisprudence path doesn't consume templates today — ECLI shape
    // varies by court enough that a single template syntax can't
    // capture it. Lawyer overrides for jurisprudence land in a
    // dedicated follow-up.
    return renderJurisprudence(refKey, label ?? null)
  }
  return renderArticle(
    refKey,
    codeName ?? null,
    articleNumber ?? null,
    template,
  )
}
